"use client";

import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { t } from "@/lib/i18n";
import { useLang } from "../../LangProvider";
import FloatingControls from "../../FloatingControls";
import PasswordInput from "@/components/PasswordInput";

const inputCls =
  "w-full rounded-lg border border-[var(--p-input-border)] bg-[var(--p-input-bg)] px-3 py-2 text-sm text-[var(--p-input-text)] placeholder:text-[var(--p-input-ph)] outline-none focus:border-[var(--p-heading)]";

export default function LoginForm({
  google,
  facebook,
}: {
  google: boolean;
  facebook: boolean;
}) {
  const lang = useLang();
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await signIn("credentials", {
      email,
      password,
      redirect: false,
    });
    setLoading(false);
    if (res?.error) {
      setError(t(lang, "login.error"));
      return;
    }
    router.push("/account");
    router.refresh();
  }

  const anyOauth = google || facebook;

  return (
    <main className="flex min-h-screen items-center justify-center p-6">
      <FloatingControls />
      <div className="w-full max-w-sm rounded-2xl border border-[var(--p-card-border)] bg-[var(--p-card)] p-8 shadow-xl backdrop-blur">
        <h1 className="mb-1 text-2xl font-bold text-[var(--p-heading)]">
          {t(lang, "login.title")}
        </h1>
        <p className="mb-6 text-sm text-[var(--p-muted)]">
          {t(lang, "login.subtitle")}
        </p>

        {anyOauth && (
          <div className="mb-5 space-y-2">
            {google && (
              <button
                onClick={() => signIn("google", { callbackUrl: "/account" })}
                className="flex w-full items-center justify-center gap-2 rounded-full border border-[var(--p-input-border)] py-2.5 text-sm font-medium text-[var(--p-text)] transition hover:bg-[var(--p-soft)]"
              >
                {t(lang, "login.google")}
              </button>
            )}
            {facebook && (
              <button
                onClick={() => signIn("facebook", { callbackUrl: "/account" })}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1877F2] py-2.5 text-sm font-medium text-white transition hover:bg-[#166fe0]"
              >
                {t(lang, "login.facebook")}
              </button>
            )}
            <div className="flex items-center gap-3 py-2 text-xs text-[var(--p-muted)]">
              <span className="h-px flex-1 bg-[var(--p-divider)]" />{" "}
              {t(lang, "login.or")}
              <span className="h-px flex-1 bg-[var(--p-divider)]" />
            </div>
          </div>
        )}

        <form onSubmit={onSubmit}>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t(lang, "login.email")}
            className={`mb-3 ${inputCls}`}
          />
          <PasswordInput
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t(lang, "login.password")}
            className={`mb-4 ${inputCls}`}
          />
          {error && <p className="mb-3 text-sm text-red-500">{error}</p>}
          <div className="mb-4 text-right">
            <Link
              href="/forgot"
              className="text-xs text-[var(--p-muted)] hover:text-[var(--p-heading)]"
            >
              {t(lang, "login.forgot")}
            </Link>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#4B3FA0] py-3 font-medium text-white transition hover:bg-[#3d3384] disabled:opacity-60"
          >
            {loading ? t(lang, "login.submitting") : t(lang, "login.submit")}
          </button>
        </form>

        <p className="mt-5 text-center text-sm text-[var(--p-muted)]">
          {t(lang, "login.no_account")}{" "}
          <Link href="/register" className="text-[var(--p-heading)] underline">
            {t(lang, "login.register")}
          </Link>
        </p>
      </div>
    </main>
  );
}
